import { Box, Button, Heading, Image, Text } from "grommet";
import React, { ReactChildren, ReactElement } from "react";
import { useUser } from "../account/AccountStore";
import { AliasViewModel, getAliasesForUser } from "../aliases/AliasStore";
import { useDarkMode } from "../components/DarkModeSwitch";
import StyledModal from "../components/StyledModel";
import { getUserAliasesInServer, joinDiscordServer, linkDiscordAccount } from "./DiscordStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const DiscordProgress = ({ step, status, todoText, doneText, loading, children }: { step: number; status: string; todoText: string; doneText: string; loading: boolean; children: ReactElement }) => {
    return (
        <Box direction="row" gap="small" align="center" pad={{ bottom: "medium" }}>
            <Box round background="#5865F2" width={"24px"} height={"24px"} align="center">
                {step}
            </Box>
            {loading && <FontAwesomeIcon icon={["fad", "loader"]} spin></FontAwesomeIcon>}
            {!loading && status == "current" && children}
            {status == "done" && <Text>{doneText}</Text>}
            {status == "todo" && <Text color="background-front">{todoText}</Text>}
        </Box>
    );
};

const DiscordAliasLink = ({ aliasID }: { aliasID: string }) => {
    const user = useUser();

    const [aliasesInServer, setAliasesInServer] = React.useState({} as { [key: string]: boolean });
    const [discordAuthPresent, setDiscordAuthPresent] = React.useState(false);
    const [aliases, setAliases] = React.useState([] as AliasViewModel[]);

    React.useEffect(() => {
        refresh();
    }, [user]);

    const [serverCheckLoading, setServerCheckLoading] = React.useState(true);

    const refresh = () => {
        return getAliasesForUser(user.id).then((aliases) => {
            setAliases(aliases);
            if (document.cookie.includes("discord_oauth")) {
                setDiscordAuthPresent(true);
                getUserAliasesInServer().then((res) => {
                    if (!res.success) {
                        if (res.code == "DISC0001") {
                            setDiscordAuthPresent(false);
                        }
                        setErrors(res.errors);
                        setShowErrorsModal(true);
                    } else {
                        setAliasesInServer(res.result);
                    }
                    setServerCheckLoading(false);
                });
            } else {
                setServerCheckLoading(false);
            }
        });
    };

    const linkAlias = (aliasID) => {
        linkDiscordAccount(aliasID).then((res) => {
            if (!res.success) {
                if (res.code == "DISC0001") {
                    setDiscordAuthPresent(false);
                }
                setErrors(res.errors);
                setShowErrorsModal(true);
            } else {
                refresh();
            }
        });
    };

    const joinServer = () => {
        joinDiscordServer().then((res) => {
            if (!res.success) {
                if (res.code == "DISC0001") {
                    setDiscordAuthPresent(false);
                }
                setErrors(res.errors);
                setShowErrorsModal(true);
            } else {
                refresh();
            }
        });
    };

    const [errors, setErrors] = React.useState([] as string[]);
    const [showErrorsModal, setShowErrorsModal] = React.useState(false);

    const refreshDiscordAuth = () => {
        window.location.assign("/api/Discord/RefreshDiscordOauth");
    };

    const continueWithDiscord = () => {
        const scopes = encodeURIComponent(["identify", "email", "guilds.join", "guilds.members.read"].join(" "));
        const redirect_uri = "https://" + encodeURIComponent(window.location.host) + "/oauth/discord";
        let url = `https://discord.com/api/oauth2/authorize?client_id=1033992720290693201&redirect_uri=${redirect_uri}&response_type=code&scope=${scopes}`;
        window.location.assign(url);
    };

    const discordColor = "#5865F2";

    const [isDarkMode] = useDarkMode();

    const [signInStatus, setSignInStatus] = React.useState("todo" as "todo" | "current" | "done");
    const [linkStatus, setLinkStatus] = React.useState("todo" as "todo" | "current" | "done");
    const [joinStatus, setJoinStatus] = React.useState("todo" as "todo" | "current" | "done");

    const getSignInStatus = () => {
        if (discordAuthPresent) {
            return "done";
        } else {
            return "current";
        }
    };

    const getLinkStatus = () => {
        if (discordAuthPresent) {
            if (aliases.length == 0) {
                return "current";
            } else {
                if (aliases.filter((a) => a.discordID).length > 0) {
                    return "done";
                } else {
                    return "current";
                }
            }
        } else {
            return "todo";
        }
    };

    const getJoinStatus = () => {
        if (discordAuthPresent) {
            if (aliases.length == 0) {
                return "todo";
            } else {
                if (aliases.filter((a) => a.discordID).length > 0) {
                    if (aliasesInServer[aliasID]) {
                        return "done";
                    } else {
                        return "current";
                    }
                } else {
                    return "todo";
                }
            }
        } else {
            return "todo";
        }
    };

    React.useEffect(() => {
        setSignInStatus(getSignInStatus());
        setLinkStatus(getLinkStatus());
        setJoinStatus(getJoinStatus());
    }, [discordAuthPresent, aliases, aliasesInServer]);

    return (
        <Box gap="small">
            <Image onClick={() => refreshDiscordAuth()} src={isDarkMode ? "/images/discord-logo-white.png" : "/images/discord-logo-black.png"} width="80px"></Image>
            <Box direction="row" align="center" gap="medium" wrap>
                <DiscordProgress loading={false} step={1} status={signInStatus} todoText="Sign In" doneText="Signed In">
                    <Button primary color={discordColor} label="Sign in with Discord" onClick={() => continueWithDiscord()}></Button>
                </DiscordProgress>
                <DiscordProgress loading={false} step={2} status={linkStatus} todoText="Link Discord Account" doneText="Discord Account Linked">
                    <Button primary color={discordColor} label="Link Discord Account" onClick={() => linkAlias(aliasID)}></Button>
                </DiscordProgress>
                <DiscordProgress loading={serverCheckLoading} step={3} status={joinStatus} todoText="Join Server" doneText="Server Joined">
                    <Button primary color={discordColor} label="Join Discord Server" onClick={() => joinServer()}></Button>
                </DiscordProgress>
            </Box>

            <StyledModal isOpen={showErrorsModal} setIsOpen={() => setShowErrorsModal(false)}>
                <Heading level={3}>Errors</Heading>
                {errors.map((e) => (
                    <Text>{e}</Text>
                ))}
                {!discordAuthPresent && (
                    <Box align="center" justify="center" gap="medium">
                        <Image src="/images/discord-logo-blue.png" width="200px"></Image>
                        <Button primary color={discordColor} label="Retry" onClick={() => refreshDiscordAuth()}></Button>
                    </Box>
                )}
                <Box direction="row" gap="small" justify="around" pad={{ top: "medium" }}>
                    <Button label="Close" onClick={() => setShowErrorsModal(false)}></Button>
                </Box>
            </StyledModal>
        </Box>
    );
};

export default DiscordAliasLink;
